import { useEffect, useState } from "react";
import { PROBE_TIMEOUT_MS, releaseVideo } from "./vreEditSession";

const IDLE = { status: "idle", duration: 0, width: 0, height: 0 };

/**
 * A video's length and frame size, read from a throw-away <video preload="metadata">.
 *
 * Returns { status: "idle" | "loading" | "ready" | "error", duration, width, height }.
 * `enabled: false` reads nothing and stays "idle"; an enabled hook with no URL
 * is an "error", because there is nothing it could ever read.
 */
export default function useVideoMetadata(url, enabled = true) {
  const [probe, setProbe] = useState({ url: null, ...IDLE });

  useEffect(() => {
    if (!enabled) {
      setProbe({ url, ...IDLE });
      return undefined;
    }
    if (!url) {
      setProbe({ url, ...IDLE, status: "error" });
      return undefined;
    }
    setProbe({ url, ...IDLE, status: "loading" });
    const vid = document.createElement("video");
    let timer = null;
    const detach = () => {
      clearTimeout(timer);
      vid.onloadedmetadata = null;
      vid.onerror = null;
      releaseVideo(vid);
    };
    vid.preload = "metadata";
    vid.onloadedmetadata = () => {
      const duration = Number.isFinite(vid.duration) ? vid.duration : 0;
      const { videoWidth: width, videoHeight: height } = vid;
      detach();
      setProbe({ url, status: "ready", duration, width: width || 0, height: height || 0 });
    };
    vid.onerror = (e) => {
      console.warn("Failed to load video metadata:", e);
      detach();
      setProbe({ url, ...IDLE, status: "error" });
    };
    // Never wait forever: a video that does not answer reads as an error.
    timer = setTimeout(() => {
      detach();
      setProbe({ url, ...IDLE, status: "error" });
    }, PROBE_TIMEOUT_MS);
    vid.src = url;
    return detach;
  }, [url, enabled]);

  if (!enabled) return IDLE;
  return probe.url === url ? probe : { ...IDLE, status: "loading" };
}
